import * as React from "react"
import { useSelector } from "react-redux"

import PrimaryButton from "../PrimaryButton"
import TextDialog from "../TextDialog"
import TextAreaBlock from "../TextAreaBlock"

import { dispatch } from "../../store"
import { userInfoActions } from "../../actions";
import api from "../../utils/api"
import { isDesktopWidth } from "../../utils"
import * as AppTypes from "AppTypes"

const SHORT_CONTENT_LENGTH = 100

interface IBiographyProps {
    firstLoad: boolean
}

const Biography: React.FC<IBiographyProps> = props => {
    const { userInfo, biographyLimit, langPack } = useSelector((store: AppTypes.ReducerState) => ({
        userInfo: store.userInfo || {},
        biographyLimit: store.config.appConfig.biographyLimit,
        langPack: store.config.langPack
    }))
    const { firstLoad } = props;
    const { artistInfo = {} } = userInfo
    const [isFullView, setIsFullView] = React.useState(false)
    const [warning, setBioWarning] = React.useState(true)

    const [open, setOpen] = React.useState(false);
    const [desktopView, setDesktopView] = React.useState(false)

    React.useEffect(() => {
        const detect = () => setDesktopView(isDesktopWidth())
        detect()
        window.addEventListener("resize", detect)
        return () => window.removeEventListener("resize", detect)
    }, [])

    const handleClickOpen = () => {
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
    };

    const saveHandler = (value: string) => {
        dispatch(userInfoActions.updateArtistInfo({ biography: value }))
        userInfo.artistInfo.biography = value
        api.updateApplication(userInfo)
        setBioWarning(false);
        setOpen(false)
    }

    const desktopSaveHandler = (value: string) => {
        dispatch(userInfoActions.updateArtistInfo({ biography: value }))
        setBioWarning(false);
    }

    const isShortContent = artistInfo.biography && artistInfo.biography.length < SHORT_CONTENT_LENGTH

    const title = langPack["artistBio"]
    const description = langPack["shareWithUs"]
    const warningText = langPack["artistBioWarning"];

    const MobileView = () => {
        return (
            <React.Fragment>
                <div className="dashboard-content" style={{ marginTop: 8, marginBottom: 8 }}>
                    {!artistInfo.biography
                        ? <div className="dashboard__biography">
                            <h2 className={`dashboard__title ${warning ? "dashboard__title--warning" : ""}`}>
                                {title}
                            </h2>
                            <p className="dashboard__sub-title">
                                {description}
                            </p>
                            <div className="dashboard-action">
                                <PrimaryButton
                                    className="button--blue"
                                    text={langPack["buttonAddBio"]}
                                    onClick={handleClickOpen}
                                />

                            </div>
                        </div>

                        : <div className="dashboard__biography">
                            <h2 className={`dashboard__title ${warning ? "dashboard__title--warning" : ""}`}>
                                {langPack["artistBio"]}
                            </h2>
                            <div className="text">
                                <p className={`description ${(isFullView || isShortContent) ? `long` : `short`}`}>
                                    {(isFullView || isShortContent)
                                        ? artistInfo.biography
                                        : artistInfo.biography.slice(0, SHORT_CONTENT_LENGTH) + "..."}
                                </p>
                                <span className="btn-edit" onClick={handleClickOpen}>
                                    <img src="images/edit.svg" alt="" style={{ width: "22px", height: "25px" }} />
                                </span>
                            </div>
                            {!isShortContent && <div className="btn-view" onClick={() => setIsFullView(!isFullView)}>
                                {`${isFullView ? `VIEW LESS` : `VIEW MORE`}`}
                            </div>}
                        </div>
                    }
                </div>
                <TextDialog
                    title={title}
                    description={description}
                    maxLength={biographyLimit}
                    contentSource={artistInfo.biography}
                    open={open}
                    onClose={handleClose}
                    onSave={saveHandler}
                    placeholderText={langPack["artistBioPlaceholder"]}
                />
            </React.Fragment>
        )
    }

    const DesktopView = () => {
        return <TextAreaBlock
            inputId={"biography"}
            title={title}
            description={description}
            value={artistInfo.biography}
            key={"biography"}
            warningText={warningText}
            allowEmpty={false}
            allowMaxLengthOnly={true}
            firstLoad={firstLoad}
            maxLength={biographyLimit}
            onSave={desktopSaveHandler}
            api={api.updateBiography}
            apiCallBack={
                (value) => {
                    const stored = window.sessionStorage.getItem("application-store")
                    if (stored) {
                        const persistStore: AppTypes.ReducerState = JSON.parse(stored)
                        persistStore.userInfo.artistInfo.biography = value
                        window.sessionStorage.setItem("application-store", JSON.stringify(persistStore))
                    }
                }
            }
            placeholderText={langPack["artistBioPlaceholder"]}
        />
    }

    return (
        desktopView
            ? <DesktopView />
            : <MobileView />
    )
}

export default Biography
